/// <amd-module name="Wit/WorkItemForm/Common/ImageUploader" />
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { DalFields } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

const attachmentChunkSize = 25000000; // 25M
const validFileTypeList = ["jpg", "jpeg", "png", "bmp", "gif", "tif", "tiff"];
const fileTypeToExtension: { [key: string]: string } = {
    "image/jpg": "jpg",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/bmp": "bmp",
    "image/gif": "gif",
    "image/tif": "tif",
    "image/tiff": "tif"
};

export class ImageUploader {
    private _workItem: IWorkItem;
    private _pageContext: IVssPageContext;

    public constructor(pageContext: IVssPageContext, workItem: IWorkItem) {
        this._pageContext = pageContext;
        this._workItem = workItem;
    }

    public async upload(file: File, options?: {useAreaPath?: boolean}): Promise<string> {
        // post the attachment contents
        if (!file || !isValidFileType(file.type)) {
            const error = getInvalidFileTypeMessage();
            alert(error);
            return Promise.reject(error);
        }

        const httpClient = getWorkItemTrackingClient(this._pageContext);
        const areaPath = options?.useAreaPath ? this._workItem.getFieldValueById(DalFields.AreaPathId) : undefined;

        // Chunk file upload if we are using a browser that supports File slicing
        const chunkSize = typeof file.slice === "function" && file.size > attachmentChunkSize ? attachmentChunkSize : undefined;

        // IE11 will specify empty file name when pasting
        const fileName = file.name ?? "image." + fileTypeToExtension[file.type] ?? "png";

        const attachmentReferencePromise = httpClient.createAttachment(file, this._workItem.project.guid, fileName, "Simple", areaPath);
        this._workItem.addPromiseToWait(attachmentReferencePromise);

        const attachmentReference = await attachmentReferencePromise;
        return attachmentReference.url;
    }
}

/**
 * Check if the selected file type is supported as an image file
 */
export function isValidFileType(fileType: string): boolean {
    const validFileTypes = new RegExp("^.*(" + validFileTypeList.join("|") + ")$");
    return validFileTypes.test(fileType.toLowerCase());
}

/**
 * Get the error message with a list of image file types supported
 */
export function getInvalidFileTypeMessage(): string {
    return format(Resources.InsertImageInvalidFileType, validFileTypeList.join(", "));
}
